import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, GitMerge, Warning } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * DuplicatesBanner — shown at the top of a Job Detail when other open jobs
 * exist at the same (property_address, unit). Admin-only merge action; workers
 * only see the informational banner + link to sibling jobs.
 */
export default function DuplicatesBanner({ job, role = "worker", onMerged }) {
  const [dupes, setDupes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mergeOpen, setMergeOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/jobs/${job.id}/duplicates`);
      setDupes(r.data);
    } catch (e) {
      setDupes([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [job.id]);

  if (loading || dupes.length === 0) return null;

  const detailBase = role === "admin" ? "/admin/jobs" : "/worker/jobs";

  // Split siblings: same WO# (cross-importer double) vs. same address
  const normWO = (s) => {
    let out = String(s || "").trim().toUpperCase();
    ["W-", "WO-", "WO#", "W#", "#"].forEach((p) => { if (out.startsWith(p)) out = out.slice(p.length).trim(); });
    return out;
  };
  const thisWO = normWO(job.work_order_number || job.source_wo_number || "");
  const woMatches = thisWO
    ? dupes.filter((d) => normWO(d.work_order_number || d.source_wo_number || "") === thisWO)
    : [];
  const woMatchIds = new Set(woMatches.map((d) => d.id));
  const addrMatches = dupes.filter((d) => !woMatchIds.has(d.id));

  const heading = woMatches.length > 0
    ? (woMatches.length === 1
        ? `Duplicate WO# ${thisWO} · 1 twin found`
        : `Duplicate WO# ${thisWO} · ${woMatches.length} twins found`)
    : `${addrMatches.length} other open ${addrMatches.length === 1 ? "job" : "jobs"} at this ${job.unit ? "unit" : "property"}`;

  return (
    <>
      <div className="mt-3 p-3 border-2 border-fuchsia-500 bg-fuchsia-50" data-testid="duplicates-banner">
        <div className="flex items-start gap-2">
          <Copy weight="fill" size={20} className="text-fuchsia-600 flex-shrink-0 mt-0.5"/>
          <div className="flex-1 min-w-0">
            <div className="font-black uppercase tracking-widest text-xs text-fuchsia-800">
              {heading}
            </div>
            <ul className="mt-1 space-y-1">
              {dupes.map(d => {
                const isTwin = woMatchIds.has(d.id);
                return (
                <li key={d.id} className="text-sm">
                  <Link to={`${detailBase}/${d.id}`} className="inline-flex items-center gap-1.5 hover:underline flex-wrap"
                    data-testid={`dupes-sibling-${d.id}`}>
                    <span className={`inline-flex items-center h-4 px-1 text-white text-[9px] font-black uppercase tracking-widest font-mono ${isTwin ? "bg-fuchsia-700" : "bg-[#0f172a]"}`}>
                      WO# {d.work_order_number || d.id.slice(-6)}
                    </span>
                    {isTwin && (
                      <span className="inline-flex items-center h-4 px-1 bg-fuchsia-100 border border-fuchsia-500 text-fuchsia-800 text-[9px] font-black uppercase tracking-widest">
                        WO Twin
                      </span>
                    )}
                    {d.life_safety && (
                      <span className="inline-flex items-center gap-0.5 h-4 px-1 bg-red-600 text-white text-[9px] font-black uppercase tracking-widest">
                        <Warning weight="fill" size={9}/> Safety
                      </span>
                    )}
                    <span className="font-black truncate">{d.title}</span>
                    <span className="text-[10px] text-slate-500 whitespace-nowrap">· {(d.created_at || "").slice(0, 10)} · {d.status}</span>
                  </Link>
                </li>
              );})}
            </ul>
          </div>
          {role === "admin" && (
            <button onClick={() => setMergeOpen(true)} data-testid="dupes-merge-btn"
              className="h-9 px-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase text-[11px] tracking-widest whitespace-nowrap inline-flex items-center gap-1">
              <GitMerge weight="bold" size={12}/> Merge
            </button>
          )}
        </div>
      </div>
      {mergeOpen && (
        <QuickMergeModal thisJob={job} dupes={dupes}
          onClose={() => setMergeOpen(false)}
          onDone={() => { setMergeOpen(false); load(); onMerged && onMerged(); }}/>
      )}
    </>
  );
}

function QuickMergeModal({ thisJob, dupes, onClose, onDone }) {
  // Pool = [thisJob] + dupes. Master = oldest by created_at.
  const pool = [thisJob, ...dupes].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
  const [masterId, setMasterId] = useState(pool[0]?.id || thisJob.id);
  const [childIds, setChildIds] = useState(new Set(pool.slice(1).map(j => j.id)));
  const [busy, setBusy] = useState(false);

  const toggleChild = (id) => {
    setChildIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const changeMaster = (id) => {
    setMasterId(id);
    setChildIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };
  const submit = async () => {
    if (!masterId || childIds.size === 0) {
      toast.error("Pick a master and at least one child");
      return;
    }
    setBusy(true);
    try {
      await api.post("/jobs/merge", { master_id: masterId, child_ids: Array.from(childIds) });
      toast.success(`Merged ${childIds.size} WO${childIds.size === 1 ? "" : "s"}`);
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not merge");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg border-4 border-[#0f172a] shadow-[6px_6px_0px_#00C4CC] max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} data-testid="merge-modal">
        <div className="p-4 border-b-2 border-slate-200">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Merge Duplicates</div>
          <div className="font-display font-black text-xl truncate">{thisJob.property_address}{thisJob.unit ? ` · #${thisJob.unit}` : ""}</div>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-xs text-slate-600">
            Photos, checklists, notes and descriptions from every child are folded into the master. Children stay viewable but marked <span className="font-mono bg-slate-100 px-1">merged</span>.
          </p>
          {pool.map(j => {
            const isMaster = j.id === masterId;
            const isChild = childIds.has(j.id);
            return (
              <div key={j.id} className={`p-2 border-2 ${isMaster ? "border-[#00C4CC] bg-cyan-50" : isChild ? "border-fuchsia-400 bg-fuchsia-50" : "border-slate-200"}`}
                data-testid={`merge-row-${j.id}`}>
                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest cursor-pointer">
                    <input type="radio" name="master" checked={isMaster} onChange={() => changeMaster(j.id)}/>Master
                  </label>
                  <label className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest cursor-pointer">
                    <input type="checkbox" checked={isChild} disabled={isMaster}
                      onChange={() => toggleChild(j.id)}/>Fold in
                  </label>
                  <span className="ml-auto inline-flex items-center h-5 px-1.5 bg-[#0f172a] text-white text-[10px] font-black uppercase tracking-widest font-mono">
                    WO# {j.work_order_number || j.id.slice(-6)}
                  </span>
                </div>
                <div className="mt-1 font-black text-sm truncate">{j.title}</div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  {(j.created_at || "").slice(0, 10)} · {j.status}
                </div>
              </div>
            );
          })}
          <button onClick={submit} disabled={busy || !masterId || childIds.size === 0}
            data-testid="merge-submit"
            className="w-full h-12 bg-gradient-to-r from-[#0f172a] to-fuchsia-600 text-white font-black uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
            <GitMerge weight="fill" size={16}/> {busy ? "Merging…" : `Merge ${childIds.size} into master`}
          </button>
        </div>
      </div>
    </div>
  );
}
